import { Link, useLocation } from "react-router-dom";

/* ─── Cymatic SVG Icons (brightened) ─── */
const CymRoot = ({ color, size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="10" stroke={color} strokeWidth="1.2" opacity="0.6" />
    <circle cx="12" cy="12" r="7" stroke={color} strokeWidth="1.2" opacity="0.75" />
    <circle cx="12" cy="12" r="4" stroke={color} strokeWidth="1.5" opacity="0.9" />
    <circle cx="12" cy="12" r="1.5" fill={color} />
  </svg>
);

const CymSacral = ({ color, size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <polygon points="12,2 22,8 22,16 12,22 2,16 2,8" stroke={color} strokeWidth="1.2" opacity="0.65" />
    <polygon points="12,5 19,9 19,15 12,19 5,15 5,9" stroke={color} strokeWidth="1.2" opacity="0.8" />
    <circle cx="12" cy="12" r="2" fill={color} />
  </svg>
);

const CymSolar = ({ color, size = 22 }) => {
  const rays = Array.from({ length: 8 }, (_, i) => {
    const a = (i * 45 * Math.PI) / 180;
    return `M12,12 L${12 + Math.cos(a) * 10},${12 + Math.sin(a) * 10}`;
  }).join(" ");
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d={rays} stroke={color} strokeWidth="1" opacity="0.65" />
      <circle cx="12" cy="12" r="4" stroke={color} strokeWidth="1.4" opacity="0.9" />
      <circle cx="12" cy="12" r="1.5" fill={color} />
    </svg>
  );
};

const CymHeart = ({ color, size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    {[0, 60, 120, 180, 240, 300].map(a => {
      const r = (a * Math.PI) / 180;
      const cx = 12 + Math.cos(r) * 5;
      const cy = 12 + Math.sin(r) * 5;
      return <circle key={a} cx={cx} cy={cy} r="3.5" stroke={color} strokeWidth="1" opacity="0.65" />;
    })}
    <circle cx="12" cy="12" r="1.5" fill={color} />
  </svg>
);

const CymThroat = ({ color, size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path d="M2,12 Q6,6 12,12 Q18,18 22,12" stroke={color} strokeWidth="1.2" opacity="0.65" />
    <path d="M2,12 Q6,18 12,12 Q18,6 22,12" stroke={color} strokeWidth="1.2" opacity="0.65" />
    <circle cx="12" cy="12" r="6" stroke={color} strokeWidth="1" opacity="0.55" />
    <circle cx="12" cy="12" r="2" fill={color} />
  </svg>
);

const CymThirdEye = ({ color, size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path d="M2,12 Q12,4 22,12 Q12,20 2,12Z" stroke={color} strokeWidth="1.2" opacity="0.7" />
    <circle cx="12" cy="12" r="4" stroke={color} strokeWidth="1.2" opacity="0.8" />
    <circle cx="12" cy="12" r="1.5" fill={color} />
  </svg>
);

const CymCrown = ({ color, size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="10" stroke={color} strokeWidth="0.8" opacity="0.45" />
    {[0, 45, 90, 135, 180, 225, 270, 315].map(a => {
      const r = (a * Math.PI) / 180;
      return <line key={a} x1={12 + Math.cos(r) * 3} y1={12 + Math.sin(r) * 3} x2={12 + Math.cos(r) * 10} y2={12 + Math.sin(r) * 10} stroke={color} strokeWidth="0.9" opacity="0.6" />;
    })}
    <circle cx="12" cy="12" r="3" stroke={color} strokeWidth="1.2" opacity="0.8" />
    <circle cx="12" cy="12" r="1" fill={color} />
  </svg>
);

/* ─── Tabs (reordered for visual flow) ─── */
const TABS = [
  { path: "/", label: "HOME", Icon: CymRoot, color: "#FF0000" },
  { path: "/sound", label: "SOUND", Icon: CymSolar, color: "#FFD700" },
  { path: "/codes", label: "CODES", Icon: CymSacral, color: "#FF6600" },
  { path: "/godmode", label: "GODMODE", Icon: CymThroat, color: "#0088FF" },
  { path: "/resonance", label: "BI-NAURAL", Icon: CymHeart, color: "#00CC44" },
  { path: "/mirrors", label: "SYNC", Icon: CymThirdEye, color: "#7733FF" },
  { path: "/how-it-works", label: "HOW", Icon: CymCrown, color: "#AA44FF" },
];

export default function MobiusNav() {
  const location = useLocation();

  return (
    <nav
      className="fixed left-0 right-0 z-[9999]"
      style={{ bottom: "44px" }}
      data-testid="bottom-nav"
    >
      <div className="bg-black/95 backdrop-blur-sm border-t border-[#FF0000]/15 pb-[env(safe-area-inset-bottom,0px)]">
        <div className="mx-auto max-w-lg flex items-center justify-between px-2 sm:px-3 py-1.5">
          {TABS.map((tab) => {
            const { Icon } = tab;
            const isActive =
              location.pathname === tab.path ||
              (tab.path === "/library" && location.pathname === "/archive");

            return (
              <Link
                key={tab.path}
                to={tab.path}
                className="flex flex-col items-center gap-1 min-w-[40px] py-1 transition-all duration-200 group"
                data-testid={`nav-${tab.label.toLowerCase().replace(/[- ]/g, "-")}`}
              >
                <div className="relative">
                  <Icon
                    color={isActive ? tab.color : tab.color}
                    size={22}
                  />
                  {/* Active glow */}
                  {isActive && (
                    <div
                      className="absolute inset-0 blur-lg rounded-full -z-10"
                      style={{ backgroundColor: `${tab.color}55` }}
                    />
                  )}
                  {/* Dim overlay for inactive */}
                  {!isActive && (
                    <div className="absolute inset-0 bg-black/40 rounded-full -z-10" />
                  )}
                </div>
                <span
                  className="text-[7px] sm:text-[8px] tracking-[0.15em] uppercase font-mono leading-none transition-colors font-medium"
                  style={{
                    color: isActive ? tab.color : `${tab.color}BB`,
                  }}
                >
                  {tab.label}
                </span>
                {/* Active dot indicator */}
                {isActive && (
                  <div
                    className="w-1 h-1 rounded-full"
                    style={{ backgroundColor: tab.color }}
                  />
                )}
              </Link>
            );
          })}
        </div>
      </div>
    </nav>
  );
}
